import { Check, ChevronDown, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/utils.js';
import { Badge } from './badge.js';
import { Button } from './button.js';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command.js';
import { clearValues, removeValue, toggleValue } from './multi-select-helpers.js';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
import { Separator } from './separator.js';

export interface MultiSelectOption {
  /** The unique value associated with the option. */
  value: string;
  /** The text shown for the option (in the chip and the search list). */
  label: string;
  /** Optional muted secondary line shown under the label in the search list. */
  description?: string;
  /** When true, the option is shown but cannot be selected. */
  disabled?: boolean;
}

export interface MultiSelectProps {
  /** Options to choose from. */
  options: MultiSelectOption[];
  /** Currently selected values (controlled). */
  value: string[];
  /** Called with the next selection whenever it changes. */
  onValueChange: (value: string[]) => void;
  /** Empty-state text shown in the trigger when nothing is selected. */
  placeholder?: string;
  /** Placeholder for the search input. */
  searchPlaceholder?: string;
  /** Shown when no option matches the search. */
  emptyIndicator?: React.ReactNode;
  disabled?: boolean;
  /** Applied to the trigger box. */
  className?: string;
  /** Associates the trigger with an external label. */
  id?: string;
  /** Set inside a modal dialog, whose scroll lock otherwise stops the list scrolling. */
  modal?: boolean;
}

/**
 * A reusable chip-style multi-select: selected options render as removable
 * badges in an input-like trigger; a search popover toggles selection. Domain
 * agnostic — it only knows `string[]` values — so any "pick N from a known set"
 * UI (recipients, filters, tags, assignees) can use it. Adapted (trimmed) from
 * the shadcn-multi-select community pattern; no animations, groups, or
 * select-all in this version.
 */
export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = 'Select…',
      searchPlaceholder = 'Search…',
      emptyIndicator = 'No results found.',
      disabled,
      className,
      id,
      modal,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');

    const optionByValue = React.useMemo(
      () => new Map(options.map((option) => [option.value, option])),
      [options],
    );
    const selected = React.useMemo(() => new Set(value), [value]);

    const handleRemove = (next: string) => onValueChange(removeValue(value, next));

    // Backspace on an empty search removes the last selected chip.
    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const last = value[value.length - 1];
      if (event.key === 'Backspace' && search === '' && last !== undefined) {
        handleRemove(last);
      }
    };

    return (
      <Popover open={open} onOpenChange={setOpen} modal={modal}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            id={id}
            type="button"
            variant="outline"
            aria-haspopup="listbox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'flex h-auto min-h-10 w-full items-center justify-between gap-1 p-1 font-normal hover:bg-background',
              className,
            )}
          >
            {value.length > 0 ? (
              <div className="flex flex-1 flex-wrap items-center gap-1">
                {value.map((selectedValue) => {
                  const option = optionByValue.get(selectedValue);
                  if (!option) return null;
                  return (
                    <Badge key={selectedValue} variant="secondary" className="gap-1 pr-1">
                      {option.label}
                      {/* biome-ignore lint/a11y/useSemanticElements: a real <button> would nest inside the trigger button, which is invalid HTML. */}
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${option.label}`}
                        className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRemove(selectedValue);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            handleRemove(selectedValue);
                          }
                        }}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <span className="px-2 text-muted-foreground">{placeholder}</span>
            )}
            <div className="flex shrink-0 items-center self-stretch">
              {value.length > 0 ? (
                <>
                  {/* biome-ignore lint/a11y/useSemanticElements: nested inside the trigger button; a semantic button would be invalid nested-interactive HTML. */}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Clear all"
                    className="flex items-center rounded-sm px-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    onClick={(event) => {
                      event.stopPropagation();
                      onValueChange(clearValues());
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onValueChange(clearValues());
                      }
                    }}
                  >
                    <X className="h-4 w-4" />
                  </span>
                  <Separator orientation="vertical" className="h-6" />
                </>
              ) : null}
              <ChevronDown className="mx-2 h-4 w-4 shrink-0 text-muted-foreground" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              onKeyDown={handleSearchKeyDown}
            />
            <CommandList>
              <CommandEmpty>{emptyIndicator}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected.has(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={`${option.label} ${option.value}`}
                      disabled={option.disabled}
                      onSelect={() => onValueChange(toggleValue(value, option.value))}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible',
                        )}
                      >
                        <Check className="h-3 w-3" />
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{option.label}</span>
                        {option.description ? (
                          <span className="truncate text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        ) : null}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);
MultiSelect.displayName = 'MultiSelect';
